import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, SignupData, Team, TeamMembership } from '../types/api';
import { apiService } from '../services/api';
import { ApiError } from '../services/ApiError';
import { markSessionActive, clearSessionHint } from '../utils/sessionHint';
import { isNative } from '../services/nativeAuth';
import { authorizeWithApple, startWebSignIn, readAppleWebResult, clearAppleWebResult } from '../services/appleSignIn';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  currentTeam: Team | null;
  availableTeams: Team[];
  userMemberships: TeamMembership[];
  login: (email: string, password: string) => Promise<boolean>;
  loginWithApple: () => Promise<boolean>;
  appleError: boolean;
  clearAppleError: () => void;
  signup: (userData: SignupData) => Promise<boolean>;
  logout: () => void;
  requestPasswordReset: (email: string) => Promise<boolean>;
  resetPassword: (token: string, newPassword: string) => Promise<boolean>;
  verifyEmail: (token: string) => Promise<boolean>;
  resendVerification: () => Promise<boolean>;
  switchTeam: (teamId: number) => Promise<void>;
  refreshUserTeams: () => Promise<void>;
  updateMyProfile: (userData: Partial<Pick<User, 'firstName' | 'lastName'>>) => Promise<boolean>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [userMemberships, setUserMemberships] = useState<TeamMembership[]>([]);
  const [loading, setLoading] = useState(true);
  // Alleen gezet als de Apple-webflow met een fout terugkwam; de UI toont dan één melding.
  const [appleError, setAppleError] = useState(false);

  const loadUserTeams = async () => {
    try {
      const memberships = await apiService.getUserTeams();
      setUserMemberships(memberships);
      const teams = memberships.map(membership => membership.team);
      setAvailableTeams(teams);
      
      // Only active teams can be selected. If every team is inactive we fall back to
      // the full list, otherwise the app would be left without a current team.
      const selectableTeams = teams.some(t => t.isActive !== false)
        ? teams.filter(t => t.isActive !== false)
        : teams;

      // Set current team from localStorage or first selectable team
      const savedTeamId = localStorage.getItem('currentTeamId');
      let targetTeam: Team | null = null;

      const savedTeam = savedTeamId
        ? selectableTeams.find(t => t.teamId === parseInt(savedTeamId))
        : undefined;

      if (savedTeam) {
        targetTeam = savedTeam;
      } else if (selectableTeams.length > 0) {
        targetTeam = selectableTeams[0];
        localStorage.setItem('currentTeamId', selectableTeams[0].teamId.toString());
      }

      // If we have a target team, fetch complete team data with club information
      if (targetTeam) {
        try {
          const completeTeam = await apiService.getTeam(targetTeam.teamId);
          setCurrentTeam(completeTeam);
          
          if (process.env.NODE_ENV !== 'production') {
            console.log('Loaded complete team data on reload:', completeTeam.name, 'has club:', !!completeTeam.club);
          }
        } catch (error) {
          console.error('Failed to fetch complete team data, using basic team:', error);
          // Fallback to basic team data
          setCurrentTeam(targetTeam);
        }
      }
    } catch (error) {
      console.error('Failed to load user teams:', error);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Try to get current user - will fail if no valid cookie
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
        setIsAuthenticated(true);
        // Ook hier, niet alleen na het inloggen: zo krijgen bestaande sessies het
        // spoor bij hun eerste bezoek en klopt de vervaldatum weer.
        markSessionActive();
        await loadUserTeams();
      } catch (error) {
        // No valid authentication cookie
        clearSessionHint();
      } finally {
        setLoading(false);
      }
    };

    // Listen for auth failures from API service
    const handleAuthFailure = () => {
      clearSessionHint();
      setIsAuthenticated(false);
      setUser(null);
      setCurrentTeam(null);
      setAvailableTeams([]);
      setUserMemberships([]);
      // Could redirect to login page here
    };

    // Terugkeer uit de Apple-webflow. De backend heeft de sessiecookies al gezet, dus bij
    // ?apple=ok volstaat het om de gebruiker opnieuw op te halen. De parameter gaat er
    // meteen uit, zodat een herlaadactie niet nog eens een melding toont.
    const appleResult = readAppleWebResult();
    if (appleResult) {
      clearAppleWebResult();
      if (appleResult === 'error') {
        setAppleError(true);
      }
    }

    window.addEventListener('auth-failure', handleAuthFailure);
    initializeAuth();

    return () => {
      window.removeEventListener('auth-failure', handleAuthFailure);
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await apiService.login(email, password);
      setUser(response.user);
      setIsAuthenticated(true);
      markSessionActive();

      // Load user teams after successful login
      await loadUserTeams();
      
      return true;
    } catch (error) {
      // Een `ApiError` draagt de reden van de backend mee ("onjuiste inloggegevens" versus
      // "dit account gebruikt Inloggen met Apple"). Die moet het inlogscherm kunnen tonen,
      // dus die gaat door in plaats van te verdwijnen achter een kale `false`.
      if (error instanceof ApiError) {
        throw error;
      }
      console.error('Login failed:', error);
      return false;
    }
  };

  /**
   * Native: de systeemdialoog tonen en de code inwisselen. Web: doorsturen naar de backend,
   * die de aanmelding bij Apple begint — die tak keert niet terug, de pagina navigeert weg.
   *
   * Een annulering komt hier als `AppleSignInCancelled` naar boven, zodat de aanroeper er
   * geen foutmelding van maakt.
   */
  const loginWithApple = async (): Promise<boolean> => {
    if (!isNative()) {
      startWebSignIn(apiService.getApiBaseUrl(), window.location.pathname + window.location.search);
      return false;
    }

    const { code, givenName, familyName } = await authorizeWithApple();
    const response = await apiService.loginWithApple(code, { givenName, familyName });

    setUser(response.user);
    setIsAuthenticated(true);
    markSessionActive();
    await loadUserTeams();

    return true;
  };

  const signup = async (userData: SignupData): Promise<boolean> => {
    try {
      await apiService.register(userData);
      return true;
    } catch (error) {
      console.error('Registration failed:', error);
      return false;
    }
  };

  const logout = () => {
    apiService.logout();
    clearSessionHint();
    setIsAuthenticated(false);
    setUser(null);
    setCurrentTeam(null);
    setAvailableTeams([]);
    setUserMemberships([]);
    localStorage.removeItem('currentTeamId'); // Keep non-sensitive localStorage usage
  };

  const requestPasswordReset = async (email: string): Promise<boolean> => {
    try {
      await apiService.forgotPassword(email);
      return true;
    } catch (error) {
      // Zelfde reden als bij `login`: de backend legt hier uit dát dit account met Apple
      // hoort in te loggen. Zonder deze doorgeeflus werd dat "er ging iets mis".
      if (error instanceof ApiError) {
        throw error;
      }
      console.error('Password reset request failed:', error);
      return false;
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<boolean> => {
    try {
      await apiService.resetPassword(token, newPassword);
      return true;
    } catch (error) {
      console.error('Password reset failed:', error);
      return false;
    }
  };

  const verifyEmail = async (token: string): Promise<boolean> => {
    try {
      await apiService.verifyEmail(token);
      return true;
    } catch (error) {
      console.error('Email verification failed:', error);
      return false;
    }
  };

  const resendVerification = async (): Promise<boolean> => {
    try {
      await apiService.resendVerification();
      return true;
    } catch (error) {
      console.error('Resend verification failed:', error);
      return false;
    }
  };

  const switchTeam = async (teamId: number) => {
    // Het team hoeft niet in availableTeams te staan. Bij een net aangemaakt team is
    // die lijst nog van vóór het aanmaken, en dan liep dit stil dood op
    // "Team not found" - waarna er geen huidig team was en de gebruiker bijvoorbeeld
    // geen spelers kon toevoegen. We tonen wat we lokaal hebben als dat er is, en
    // halen daarna hoe dan ook de volledige gegevens op bij de server.
    const bekendTeam = availableTeams.find(t => t.teamId === teamId);

    if (bekendTeam) {
      setCurrentTeam(bekendTeam);
    }
    localStorage.setItem('currentTeamId', teamId.toString());

    try {
      const completeTeam = await apiService.getTeam(teamId);
      setCurrentTeam(completeTeam);

      window.dispatchEvent(new CustomEvent('team-switched', {
        detail: { team: completeTeam, teamId }
      }));
    } catch (error) {
      console.error('Kon de teamgegevens niet ophalen:', error);

      if (bekendTeam) {
        window.dispatchEvent(new CustomEvent('team-switched', {
          detail: { team: bekendTeam, teamId }
        }));
      }
    }
  };

  const updateMyProfile = async (userData: Partial<Pick<User, 'firstName' | 'lastName'>>): Promise<boolean> => {
    try {
      const updatedUser = await apiService.updateMyProfile(userData);
      // Samenvoegen en niet vervangen: dit endpoint geeft de kale gebruikersrij terug, zonder
      // de afgeleide velden die `/auth/me` er wél bij levert (`usesAppleSignIn`). Vervangen
      // liet het wijzigen van je naam die vlag stilletjes wissen.
      setUser(prev => (prev ? { ...prev, ...updatedUser } : updatedUser));
      return true;
    } catch (error) {
      console.error('Profile update failed:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user,
      isAuthenticated, 
      currentTeam,
      availableTeams,
      userMemberships,
      login, 
      loginWithApple,
      appleError,
      clearAppleError: () => setAppleError(false),
      signup,
      logout, 
      requestPasswordReset,
      resetPassword,
      verifyEmail,
      resendVerification,
      switchTeam,
      refreshUserTeams: loadUserTeams,
      updateMyProfile,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};